import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { catchError, Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { IGNORE_ALL_STATUSES } from '../constants/ignore-all-statuses';
import { IGNORE_STATUSES } from 'src/app/shared/http-context-tokens/ignored-statuses.http-context-token';
import { HxNotificationsService } from '@hubbix/common-components';
import { ERROR_PAGE_ROUTE_PATH } from 'src/app/shared/constants/app-routes.constant';
import { AnyValue } from '@shared/types/any-value.type';
import { HANDLE_ERRORS_IN_COMPONENT } from '../../shared/http-context-tokens/handle-errors-in-component.http-context-token';

@Injectable()
export class AppHttpErrorInterceptor implements HttpInterceptor {
  public constructor(
    private readonly router: Router,
    private readonly notification: HxNotificationsService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (request.context.get(HANDLE_ERRORS_IN_COMPONENT)) {
          return next.handle(request);
        }

        const ignoredStatuses: Array<number> | typeof IGNORE_ALL_STATUSES = request.context.get(IGNORE_STATUSES);

        if (ignoredStatuses === IGNORE_ALL_STATUSES || ignoredStatuses.includes(error.status)) {
          return of(new HttpResponse());
        }

        switch (error.status) {
          case 0:
          case 500:
          case 503:
            this.router.navigateByUrl(ERROR_PAGE_ROUTE_PATH);
            this.notification.error(
              "Une erreur s'est produite",
              'Désolé, nous avons rencontré une erreur.' +
                'Aucune action n’est nécessaire de votre côté.' +
                'Nous travaillons à résoudre ce problème.',
            );
            break;
          case 401:
            if (error.error?.hasOwnProperty('title')) {
              if (error.error.hasOwnProperty('detail')) {
                this.notifyError(error.error.title, error.error.detail);
              } else if (error.error.hasOwnProperty('message')) {
                this.notifyError(error.error.title, error.error.message);
              } else {
                this.notifyError("Une erreur s'est produite.", '');
              }
              return of(new HttpResponse());
            }
            break;
          case 406:
            if (error.error?.hasOwnProperty('title')) {
              if (error.error.hasOwnProperty('detail')) {
                this.notifyError(error.error.title, error.error.detail);
              } else if (error.error.hasOwnProperty('message')) {
                this.notifyError(error.error.title, error.error.message);
              } else {
                this.notifyError("Une erreur s'est produite.", '');
              }
              return throwError(() => new Error(error.error));
            }
            break;
          case 403:
          case 409:
          case 412:
          case 415:
          case 417:
          case 429:
            if (error.error && error.error.hasOwnProperty('title')) {
              this.notifyWarning(error.error.title);
              if (error.error.hasOwnProperty('detail')) {
                this.notifyWarning(error.error.detail);
              } else if (error.error.hasOwnProperty('message')) {
                this.notifyWarning(error.error.message);
              } else {
                this.notifyWarning("Une erreur s'est produite.");
              }
              return of(new HttpResponse());
            }
            break;
          case 404:
            if (error.error && error.error.hasOwnProperty('title')) {
              this.notifyWarning(error.error.title);
              if (error.error.hasOwnProperty('detail')) {
                this.notifyWarning(error.error.detail);
              } else if (error.error.hasOwnProperty('message')) {
                this.notifyWarning(error.error.message);
              } else {
                this.notifyWarning('La ressource demandée est introuvable.');
              }
              return of(new HttpResponse());
            }
            break;
          case 422:
            if (error.error && error.error.hasOwnProperty('title')) {
              this.notifyWarning(error.error.title);
              if (error.error.hasOwnProperty('detail')) {
                this.notifyWarning(error.error.detail);
              } else if (error.error.hasOwnProperty('message')) {
                this.notifyWarning(error.error.message);
              }
              return of(new HttpResponse());
            }
            if (error.error) {
              const keys: Array<string> = Object.keys(error.error);

              keys.forEach((error: AnyValue) => {
                this.notifyWarning(error.error[error][0]);
              });
              return of(new HttpResponse());
            }
            break;
          default:
            if (error.error && error.error.hasOwnProperty('detail')) {
              return throwError(() => new Error(error.error.detail));
            }
            if (error.error && error.error.hasOwnProperty('message')) {
              return throwError(() => new Error(error.error.message));
            }
        }
        return throwError(() => new Error(error.error));
      }),
    );
  }

  private notifyWarning(message: string): void {
    this.notification.warning('', message, {
      duration: 15000,
    });
  }

  private notifyError(title: string, message: string): void {
    this.notification.error(title, message, {
      duration: 15000,
    });
  }
}
